"use client"

import { useCallback, useEffect, useState } from "react"
import { useSession } from "next-auth/react"

import {
  GH_ANONYMOUS_TOKEN_COOKIE,
  GH_COOKIE_PREFIX,
  GH_REFRESH_TOKEN_COOKIE,
  GH_TOKEN_COOKIE,
  GH_WAS_LOGGED_COOKIE,
  parseCookieHeader,
  parseGhTokenCookie,
} from "@/app/lib/gh-cookie"
import { NATIVE_COOKIE } from "@/app/lib/native/native-mode"
import { useGamingHub } from "@/app/providers/GamingHubProvider"
import { useNative } from "@/app/providers/NativeProvider"

/**
 * Read-only view of the three things that have to agree for the game to look
 * signed in: the Gaming Hub auth source, the cookie it leaves behind, and the
 * server session `auth()` derives from that cookie. When they disagree, this
 * says which leg is missing.
 *
 * Off unless `?auth_debug=1` (sticky for the tab, so it survives the SPA's
 * in-memory navigation) or `NEXT_PUBLIC_AUTH_DEBUG` is set.
 */
const AUTH_DEBUG_QUERY = "auth_debug"
const AUTH_DEBUG_FLAG_KEY = "dreamteam-lms:auth-debug"

const ENV_ENABLED =
  process.env.NEXT_PUBLIC_AUTH_DEBUG === "1" ||
  process.env.NEXT_PUBLIC_AUTH_DEBUG === "true"

function readAuthDebugEnabled(): boolean {
  if (ENV_ENABLED) return true
  if (typeof window === "undefined") return false

  try {
    const value = new URLSearchParams(window.location.search).get(
      AUTH_DEBUG_QUERY
    )
    if (value === "1" || value === "true") {
      window.sessionStorage.setItem(AUTH_DEBUG_FLAG_KEY, "1")
      return true
    }
    if (value === "0" || value === "false") {
      window.sessionStorage.removeItem(AUTH_DEBUG_FLAG_KEY)
      return false
    }
    return window.sessionStorage.getItem(AUTH_DEBUG_FLAG_KEY) === "1"
  } catch {
    return false
  }
}

/** Enough of the token to correlate with a log line, without printing it. */
function fingerprint(token: string | undefined | null) {
  if (!token) return "—"
  return `${token.slice(0, 6)}…${token.slice(-4)} (${token.length})`
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-baseline gap-x-2">
      <span className="w-[70px] shrink-0 text-white/50">{label}</span>
      <span className="min-w-0 break-all">{children}</span>
    </div>
  )
}

type ServerProbe = { authenticated: boolean; session?: boolean }

export default function AuthDebugBar() {
  const [enabled, setEnabled] = useState(false)
  const [collapsed, setCollapsed] = useState(true)
  // Cookies are read after mount so the markup matches SSR, which cannot see
  // the client-written web-component cookie.
  const [cookies, setCookies] = useState<Map<string, string> | null>(null)
  const [probe, setProbe] = useState<ServerProbe | null>(null)

  const gh = useGamingHub()
  const native = useNative()
  const { data: session, status: sessionStatus } = useSession()

  useEffect(() => {
    setEnabled(readAuthDebugEnabled())
  }, [])

  // What the origin sees for this request, which is not the same question as
  // what the browser holds: the cookie has to survive the edge, and the user
  // then has to resolve in DynamoDB.
  const runProbe = useCallback(async () => {
    try {
      const res = await fetch("/api/auth/status?verbose=1", {
        cache: "no-store",
        credentials: "include",
      })
      setProbe(res.ok ? ((await res.json()) as ServerProbe) : null)
    } catch {
      setProbe(null)
    }
  }, [])

  useEffect(() => {
    if (!enabled) return
    void runProbe()
  }, [enabled, gh.status, sessionStatus, runProbe])

  useEffect(() => {
    if (!enabled) return

    const read = () => setCookies(parseCookieHeader(document.cookie))
    read()

    // The web component writes its cookie outside React, and the native path
    // writes it server-side, so poll rather than trust a single read.
    const timer = window.setInterval(read, 2000)
    return () => window.clearInterval(timer)
  }, [enabled, gh.status, sessionStatus])

  if (!enabled) return null

  const tokenCookie = parseGhTokenCookie(cookies?.get(GH_TOKEN_COOKIE))
  const cookieFlags = [
    [GH_TOKEN_COOKIE, cookies?.has(GH_TOKEN_COOKIE)],
    [GH_ANONYMOUS_TOKEN_COOKIE, cookies?.has(GH_ANONYMOUS_TOKEN_COOKIE)],
    [GH_REFRESH_TOKEN_COOKIE, cookies?.has(GH_REFRESH_TOKEN_COOKIE)],
    [GH_WAS_LOGGED_COOKIE, cookies?.has(GH_WAS_LOGGED_COOKIE)],
    [NATIVE_COOKIE, cookies?.has(NATIVE_COOKIE)],
  ] as const

  const source = gh.isNative ? "native bridge" : gh.isReady ? "g-h-c" : "none"
  const agrees =
    (gh.status === "authenticated") === (sessionStatus === "authenticated")

  return (
    <div className="w-full bg-[#1b1b1b] font-mono text-[11px] leading-[1.6] text-white">
      <div className="flex w-full items-center gap-2 px-3 py-1">
        <span className={agrees ? "text-emerald-400" : "text-amber-400"}>
          {agrees ? "●" : "▲"}
        </span>
        <span>
          gh:{gh.status} · session:{sessionStatus} · src:{source}
          {native.isNative ? ` · ${native.platform}` : ""}
        </span>
        {probe?.authenticated && probe.session === false && (
          <span className="text-amber-400">
            cookie reaches the server but the user lookup fails
          </span>
        )}
        <button
          type="button"
          onClick={() => void runProbe()}
          className="ml-auto underline underline-offset-2"
        >
          recheck
        </button>
        <button
          type="button"
          onClick={() => setCollapsed((value) => !value)}
          className="underline underline-offset-2"
        >
          {collapsed ? "details" : "hide"}
        </button>
        {gh.status === "authenticated" ? (
          <button type="button" onClick={gh.logout} className="underline underline-offset-2">
            logout
          </button>
        ) : (
          <button
            type="button"
            onClick={gh.login}
            disabled={!gh.isReady}
            className="underline underline-offset-2 disabled:opacity-40"
          >
            login
          </button>
        )}
      </div>

      {!collapsed && (
        <div className="grid gap-x-6 gap-y-1 border-t border-white/15 px-3 py-2 sm:grid-cols-2">
          <div className="flex flex-col gap-1">
            <div className="text-white/50">Gaming Hub (client)</div>
            <Row label="status">{gh.status}</Row>
            <Row label="ready">{String(gh.isReady)}</Row>
            <Row label="user">
              {gh.user
                ? `${gh.user.id} ${gh.user.nickname ?? gh.user.email ?? ""}`.trim()
                : "—"}
            </Row>
            <Row label="token">{fingerprint(gh.tokens?.access_token)}</Row>
          </div>

          <div className="flex flex-col gap-1">
            <div className="text-white/50">Server session (auth())</div>
            <Row label="status">{sessionStatus}</Row>
            <Row label="sees ck">
              {probe ? String(probe.authenticated) : "?"}
            </Row>
            <Row label="resolves">
              {probe?.session === undefined ? "?" : String(probe.session)}
            </Row>
            <Row label="user.id">{session?.user?.id ?? "—"}</Row>
            <Row label="dbUserId">{session?.user?.dbUserId ?? "—"}</Row>
            <Row label="email">{session?.user?.email ?? "—"}</Row>
            <Row label="roles">{session?.roles?.join(", ") || "—"}</Row>
          </div>

          <div className="flex flex-col gap-1">
            <div className="text-white/50">Cookies (prefix {GH_COOKIE_PREFIX})</div>
            <Row label="cookie">{tokenCookie ? `user_id ${tokenCookie.user_id}` : "—"}</Row>
            {cookieFlags.map(([name, present]) => (
              <Row key={name} label={present ? "set" : "missing"}>
                {name}
              </Row>
            ))}
            <div className="text-white/40">
              HttpOnly cookies (native session) are invisible here.
            </div>
          </div>

          <div className="flex flex-col gap-1">
            <div className="text-white/50">Shell</div>
            <Row label="native">{String(native.isNative)}</Row>
            <Row label="platform">{native.platform ?? "—"}</Row>
            <Row label="gameId">{native.environment?.gameId ?? "—"}</Row>
            <Row label="appVer">{native.environment?.appVersion ?? "—"}</Row>
            <Row label="env">{native.environment?.environment ?? "—"}</Row>
          </div>
        </div>
      )}
    </div>
  )
}
